import { CHANNEL_TYPES, CONNECTOR_TYPES } from '@/vars/general'
import { formatDuration } from './helpers'

export interface SendingSettings {
    type: 'channel' | 'cascade'
    id: string | number
}

/** Вариант в «Канал отправки». `value: null` — канал проекта по умолчанию. */
export interface SendOption {
    key: string
    label: string
    value: SendingSettings | null
}

interface ConnectorLike {
    connectorId: string
    connectorType: string
    channelId?: string | number | null
    phone?: string | null
    createdAt?: number | null
    isChisai?: boolean
    settings?: any
    defaultActivationSendChannel?: any
}

interface CascadeLike {
    id: number | string
    name?: string | null
    cascadeType?: string | null
    maxSendDuration?: number | null
    status?: string | null
    channels?: unknown[] | null
    createdAt?: string | null
}

const CHANNEL_NAMES: Record<string, string> = {
    [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
    [CHANNEL_TYPES.telegramChannel]: 'Telegram',
    [CHANNEL_TYPES.maxChannel]: 'MAX',
    [CHANNEL_TYPES.maxBusiness]: 'MAX Business',
    [CHANNEL_TYPES.smsChannel]: 'SMS',
}

/**
 * Без заданного срока планировщик рассылок (desktop-app, messages-scheduler)
 * берёт 60 секунд — показываем то же, что реально будет.
 */
const SCHEDULER_DEFAULT_SECONDS = 60

/**
 * «до 2 ч»: за это время каскад перебирает свои каналы — планировщик делит
 * `maxSendDuration` (в секундах) между оставшимися каналами. Ночью (21–08)
 * попытки переносятся на утро.
 */
export function cascadeDurationLabel(maxSendDuration: number | null | undefined): string {
    const seconds = maxSendDuration ?? SCHEDULER_DEFAULT_SECONDS
    return `до ${formatDuration(Math.max(1, Math.round(seconds / 60)))}`
}

/**
 * Имя канала как в списке каналов проекта и в настройке канала активаций:
 * «Telegram», второй такой же — «Telegram 2», с телефоном в скобках.
 */
function channelLabel(connector: ConnectorLike, all: ConnectorLike[]): string {
    const sameType = all
        .filter(c => !c.isChisai && c.connectorType === connector.connectorType)
        .sort((a, b) => {
            const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
            const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
            return aTime - bTime || a.connectorId.localeCompare(b.connectorId)
        })
    const index = sameType.findIndex(c => c.connectorId === connector.connectorId) + 1
    const base = CHANNEL_NAMES[connector.connectorType] || connector.connectorType
    const name = index > 1 ? `${base} ${index}` : base
    return connector.phone ? `${name} (${connector.phone})` : name
}

function cascadeLabel(cascade: CascadeLike): string {
    const name = String(cascade.name ?? '').trim() || 'Каскад'
    return `${name} · ${cascadeDurationLabel(cascade.maxSendDuration)}`
}

function readDefault(connectors: ConnectorLike[]): SendingSettings | null {
    const gcb2 = connectors.find(c => c.connectorType === CONNECTOR_TYPES.GCB2)
    let raw = gcb2?.settings?.defaultActivationSendChannel ?? gcb2?.defaultActivationSendChannel ?? null
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw)
        } catch (error) {
            raw = null
        }
    }
    const id = raw?.id ?? raw?.channelId ?? null
    if (id === null || id === undefined) return null
    return { type: raw?.type === 'channel' ? 'channel' : 'cascade', id }
}

/**
 * Варианты «Канала отправки»: канал проекта по умолчанию, каналы проекта и
 * каскады с каналами. Правила отбора — как в настройке канала активаций
 * (BotSettings): без чисай-коннекторов и WhatsApp Basis, каскады без
 * каналов не показываем.
 */
export function buildSendOptions(connectors: ConnectorLike[], cascades: CascadeLike[]): SendOption[] {
    const channelTypes = Object.values(CHANNEL_TYPES) as string[]
    const channels = connectors.filter(
        c =>
            !c.isChisai &&
            c.connectorType !== CONNECTOR_TYPES.whatsappBasis &&
            channelTypes.includes(c.connectorType) &&
            c.channelId !== undefined &&
            c.channelId !== null
    )
    const usableCascades = cascades.filter(c => Array.isArray(c.channels) && c.channels.length > 0)

    const channelOptions: SendOption[] = channels.map(c => ({
        key: `channel:${c.channelId}`,
        label: channelLabel(c, connectors),
        value: { type: 'channel', id: c.channelId as string | number },
    }))
    const cascadeOptions: SendOption[] = usableCascades.map(c => ({
        key: `cascade:${c.id}`,
        label: cascadeLabel(c),
        value: { type: 'cascade', id: c.id },
    }))

    const fallback = readDefault(connectors)
    const defaultTarget = fallback
        ? [...channelOptions, ...cascadeOptions].find(
              o => o.value!.type === fallback.type && String(o.value!.id) === String(fallback.id)
          )
        : null
    const defaultOption: SendOption = {
        key: 'default',
        label: defaultTarget ? `По умолчанию: ${defaultTarget.label}` : 'По умолчанию',
        value: null,
    }

    return [defaultOption, ...channelOptions, ...cascadeOptions]
}
